import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import { AccountCircle } from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import whatsappIcon from '../../assets/nopicture.png'
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { SocketContext, socketManager } from "../../context/Socket/SocketContext";
import UserStatusIcon from "../../components/UserModal/statusIcon";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Avatar } from "@material-ui/core";
import ForbiddenPage from "../../components/ForbiddenPage";
import AddIcon from '@mui/icons-material/Add';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    backgroundColor: theme.palette.type === 'dark' ? "#1e1e1e" : "#ffffff",
    ...theme.scrollbarStyles,
  },

  headerContainer: {
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    padding: theme.spacing(3),
    borderRadius: "0 0 20px 20px",
    color: "white",
    marginBottom: theme.spacing(3),
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  headerTitle: {
    fontSize: "2rem",
    fontWeight: 800,
    marginBottom: theme.spacing(1),
  },
  headerSubtitle: {
    fontSize: "1rem",
    opacity: 0.9,
    marginBottom: theme.spacing(2),
  },
  searchPaper: {
    padding: "4px 16px",
    display: "flex",
    alignItems: "center",
    borderRadius: "15px",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    "& .MuiInputBase-root": {
      color: "white",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(255, 255, 255, 0.7)",
      opacity: 1,
    },
  },
  addButton: {
    background: theme.palette.background.paper,
    color: "#6517ab",
    borderRadius: "12px",
    padding: "10px 24px",
    fontWeight: "700",
    textTransform: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.type === "light" ? "#f0f0f0" : "rgba(255, 255, 255, 0.1)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    },
  },

  tabContainer: {
    marginTop: theme.spacing(2),
  },
  tabs: {
    minHeight: "40px",
    "& .MuiTabs-indicator": {
      backgroundColor: "white",
      height: "3px",
      borderRadius: "3px",
    },
  },
  tab: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.7)",
    minWidth: "120px",
    minHeight: "40px",
    "&.Mui-selected": {
      color: "white",
    },
  },
  premiumCard: {
    borderRadius: "15px",
    transition: "all 0.3s ease",
    backgroundColor: theme.palette.type === 'dark' ? "#1e1e1e" : "#ffffff",
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": {
      boxShadow: "0 8px 30px rgba(101, 23, 171, 0.15)",
      transform: "translateY(-5px)",
    },
  },

  userAvatar: {
    width: 60,
    height: 60,
    margin: "0 auto",
    border: "2px solid #6517ab",
    padding: "2px",
    backgroundColor: theme.palette.background.paper,
  },

  iconButton: {
    borderRadius: "10px",
    padding: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  loadingText: {
    marginLeft: theme.spacing(2),
  },
  tableRow: {
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "rgba(101, 23, 171, 0.05)",
    },
  },
}));

const Users = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const { user: loggedInUser, socket } = useContext(AuthContext)
  const { profileImage } = loggedInUser;
  const [tabValue, setTabValue] = useState("table");

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (loggedInUser) {
      const companyId = loggedInUser.companyId;
      const onCompanyUser = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_USERS", payload: data.user });
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_USER", payload: +data.userId });
        }
      };
      socket.on(`company-${companyId}-user`, onCompanyUser);
      return () => {
        socket.off(`company-${companyId}-user`, onCompanyUser);
      };
    }
  }, [socket]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setLoadingMore(true);
    setPageNumber((prevPage) => prevPage + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const renderProfileImage = (user, size = "large") => {
    if (user.id === loggedInUser.id) {
      return (
        <Avatar
          src={`${backendUrl}/public/company${user.companyId}/user/${profileImage ? profileImage : whatsappIcon}`}
          alt={user.name}
          className={size === "large" ? classes.userAvatar : null}
          style={size === "small" ? { width: 35, height: 35 } : null}
        />
      )
    }
    return (
      <Avatar
        src={user.profileImage ? `${backendUrl}/public/company${user.companyId}/user/${user.profileImage}` : whatsappIcon}
        alt={user.name}
        className={size === "large" ? classes.userAvatar : null}
        style={size === "small" ? { width: 35, height: 35 } : null}
      />
    )
  };

  const renderCardActions = (user) => (
    <Box display="flex" gap={1} justifyContent="center" width="100%" mt={tabValue === "grid" ? 2 : 0}>
      <IconButton
        size="small"
        onClick={() => handleEditUser(user)}
        style={{ 
          backgroundColor: theme.palette.type === 'dark' ? "rgba(25, 118, 210, 0.1)" : "#e3f2fd", 
          color: "#1976d2" 
        }}
        className={classes.iconButton}
        title="Editar"
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => {
          setConfirmModalOpen(true);
          setDeletingUser(user);
        }}
        style={{ 
          backgroundColor: theme.palette.type === 'dark' ? "rgba(211, 47, 47, 0.1)" : "#ffebee", 
          color: "#d32f2f" 
        }}
        className={classes.iconButton}
        title="Excluir"
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      {loggedInUser.profile === "user" ?
        <ForbiddenPage />
        :
        <>
          <Box className={classes.headerContainer}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h5" className={classes.headerTitle}>Usuários</Typography>
                <Typography className={classes.headerSubtitle}>Gerencie sua equipe com facilidade e controle total</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={2} flexWrap="wrap" justifyContent={isMobile ? "center" : "flex-end"}>
                  <Paper className={classes.searchPaper} elevation={0}>
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "rgba(255,255,255,0.7)" }} />
                    </InputAdornment>
                    <TextField
                      placeholder="Pesquisar..."
                      value={searchParam}
                      onChange={handleSearch}
                      InputProps={{ disableUnderline: true }}
                    />
                  </Paper>
                  <Button
                    variant="contained"
                    className={classes.addButton}
                    startIcon={<AddIcon />}
                    onClick={handleOpenUserModal}
                  >
                    Novo Usuário
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <div className={classes.tabContainer}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                className={classes.tabs}
                classes={{ indicator: classes.tabIndicator }}
              >
                <Tab label="Lista" value="table" className={classes.tab} />
                <Tab label="Grade" value="grid" className={classes.tab} />
              </Tabs>
            </div>
          </Box>

          <Paper
            className={classes.mainPaper}
            elevation={0}
            onScroll={handleScroll}
          >
            {tabValue === "table" && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Avatar</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell align="center">Email</TableCell>
                    <TableCell align="center">Perfil</TableCell>
                    <TableCell align="center">Horário</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} className={classes.tableRow}>
                      <TableCell align="center">
                        {renderProfileImage(user, "small")}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" style={{ fontWeight: 500, color: theme.palette.text.primary }}>
                          {user.name}
                        </Typography>

                        <Typography variant="caption" color="textSecondary">
                          ID: {user.id}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{user.email}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={user.profile.toUpperCase()}
                          size="small"
                          style={{
                            backgroundColor: user.profile === "admin" ? "#e8f5e9" : "#e3f2fd",
                            color: user.profile === "admin" ? "#2e7d32" : "#1976d2",
                            fontWeight: 700
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {user.startWork || "00:00"} - {user.endWork || "00:00"}
                      </TableCell>
                      <TableCell align="center">
                        <UserStatusIcon user={user} />
                      </TableCell>
                      <TableCell align="center">
                        {renderCardActions(user)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={7} />}
                </TableBody>
              </Table>
            )}

            {tabValue === "grid" && (
              <Grid container spacing={3}>
                {users.map((user) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
                    <Card className={classes.premiumCard}>
                      <CardContent style={{ textAlign: 'center', flexGrow: 1 }}>
                        <Box mb={2}>
                          {renderProfileImage(user)}
                        </Box>
                        <Typography variant="h6" style={{ fontWeight: 700, color: theme.palette.text.primary }} gutterBottom>
                          {user.name}
                        </Typography>

                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          {user.email}
                        </Typography>
                        <Box display="flex" justifyContent="center" alignItems="center" gap={1} mt={1} mb={2}>
                          <Chip
                            label={user.profile.toUpperCase()}
                            size="small"
                            style={{
                              backgroundColor: user.profile === "admin" ? "#e8f5e9" : "#e3f2fd",
                              color: user.profile === "admin" ? "#2e7d32" : "#1976d2",
                              fontWeight: 700
                            }}
                          />
                          <UserStatusIcon user={user} />
                        </Box>
                        <Divider style={{ margin: '16px 0' }} />
                        <Box display="flex" flexDirection="column" gap={0.5} textAlign="left">
                          <Typography variant="caption" color="textSecondary">
                            ID: <b>{user.id}</b>
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Horário: <b>{user.startWork || "00:00"} - {user.endWork || "00:00"}</b>
                          </Typography>
                        </Box>
                        {renderCardActions(user)}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                {loadingMore && (
                  <Grid item xs={12} align="center">
                    <CircularProgress color="primary" />
                  </Grid>
                )}
              </Grid>
            )}

            {loading && !loadingMore && tabValue === "grid" && (
              <div className={classes.loadingContainer}>
                <CircularProgress color="primary" />
                <span className={classes.loadingText}>{i18n.t("loading")}</span>
              </div>
            )}
          </Paper>
        </>
      }
    </MainContainer>
  );
};

export default Users;
;